import { CurrentContractFragment } from 'generated/resources/graphql'
import { isNil } from 'lodash'

import { stringToDate } from 'modules/core/lib/dates'

import type Employee from 'modules/employees/store/models/Employee'
import type { Policies } from 'modules/payroll_bundle/store/collections/policies'
import type PolicyPeriod from 'modules/payroll_bundle/store/models/PolicyPeriod'

import { RECENT_CONTRACT_GRACE_PERIOD_DAYS } from './constants'

export function filterEmployeesByPayrollPolicyPeriod({
  employees,
  policyPeriod,
  currentContracts,
  policies,
}: {
  employees: Employee[]
  policyPeriod: PolicyPeriod
  currentContracts: CurrentContractFragment[]
  policies: Policies
}): Employee[] {
  const policy = policies.get(policyPeriod.attributes.policy_id)

  if (!policy) {
    return []
  }

  // Adds a grace period to support severance payments from the previous pay period
  const windowStartsOn = stringToDate(policyPeriod.attributes.starts_on, 'backend').subtract(
    RECENT_CONTRACT_GRACE_PERIOD_DAYS,
    'days'
  )
  const periodEndsOn = stringToDate(policyPeriod.attributes.ends_on, 'backend')

  const employeeSet: Set<number> = currentContracts.reduce(
    (employeeIds: Set<number>, contract: CurrentContractFragment) => {
      if (!contract.id) return employeeIds

      const employeeId = contract.employeeId
      if (employeeIds.has(employeeId)) return employeeIds

      const effectiveOn = stringToDate(contract.effectiveOn, 'backend')
      const isEffective = effectiveOn.isSameOrBefore(periodEndsOn, 'day')
      const endsOn = isNil(contract.endsOn) ? null : stringToDate(contract.endsOn, 'backend')
      //Reference contract may not be active, need to remove those which meet this condition.
      const activeWithin =
        isEffective && (isNil(endsOn) || endsOn.isSameOrAfter(windowStartsOn, 'day'))
      if (!activeWithin) return employeeIds

      const contractPolicyId = contract.payrollPolicy?.policy?.id

      // Check if the contract policy is the target policy
      if (contractPolicyId != policy.id) return employeeIds

      employeeIds.add(employeeId)
      return employeeIds
    },
    new Set()
  )

  return employees.filter(
    (employee: Employee) =>
      employee.activeWithinDateRange(windowStartsOn, periodEndsOn) && employeeSet.has(employee.id)
  )
}
